import React, { useState } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SaveDemoDataModal from '../modals/SaveDemoDataModal';

const DashboardLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Check if we're in demo mode
  const isDemo = location.pathname.startsWith('/demo');
  const basePrefix = isDemo ? '/demo' : '';

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleSignUpToSave = () => {
    navigate('/signup');
  };

  const desktopNavItems = [
    { path: `${basePrefix}/log`, label: 'Daily Log', icon: 'ri-file-list-line' },
    { path: `${basePrefix}/dashboard`, label: 'Dashboard', icon: 'ri-dashboard-line' },
    { path: `${basePrefix}/meal-plan`, label: 'Meal Plan', icon: 'ri-restaurant-line' },
    { path: `${basePrefix}/workout-plan`, label: 'Workout Plan', icon: 'ri-run-line' },
    { path: `${basePrefix}/vitals`, label: 'Vitals', icon: 'ri-heart-pulse-line' },
  ];

  const mobileNavItems = [
    { path: `${basePrefix}/log`, label: 'Log', icon: 'ri-file-list-3-line' },
    { path: `${basePrefix}/workout-plan`, label: 'Workout', icon: 'ri-run-line' },
    { path: `${basePrefix}/meal-plan`, label: 'Meals', icon: 'ri-restaurant-line' },
    { path: `${basePrefix}/vitals`, label: 'Vitals', icon: 'ri-heart-pulse-line' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Desktop Layout */}
      <div className="hidden lg:flex h-screen">
        {/* Left Sidebar */}
        <aside className="w-64 bg-white border-r border-neutral-200 flex flex-col">
          <div className="p-6 border-b border-neutral-200">
            <Link to={`${basePrefix}/log`} className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/e407d3d1598fd76f3f921043249236ca/002a6d99967276f3c834b5b0341ba0df.png"
                  alt="Luminous Health"
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-xl font-bold text-neutral-900">LuminousHealth</span>
            </Link>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {desktopNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer ${
                  isActive(item.path)
                    ? 'bg-primary-50 text-primary-600 font-medium'
                    : 'text-neutral-700 hover:bg-neutral-100'
                }`}
              >
                <i className={`${item.icon} text-xl`}></i>
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          <div className="p-4 border-t border-neutral-200">
            {isDemo ? (
              <div className="px-4 py-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-eye-line text-purple-600"></i>
                  <span className="text-sm font-medium text-purple-900">Demo Mode</span>
                </div>
                <Link
                  to="/signup"
                  className="block text-center w-full py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-sm font-medium hover:from-purple-600 hover:to-pink-600 transition-all whitespace-nowrap"
                >
                  Sign Up to Save
                </Link>
              </div>
            ) : (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 text-white font-semibold">
                    {user?.email?.[0].toUpperCase()}
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-sm font-medium text-gray-900 truncate">{user?.email}</div>
                    <div className="text-xs text-gray-500">View Profile</div>
                  </div>
                  <i className={`ri-arrow-${showUserMenu ? 'up' : 'down'}-s-line text-gray-400`}></i>
                </button>

                {/* User Menu Dropdown */}
                {showUserMenu && (
                  <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        logout();
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer flex items-center gap-2"
                    >
                      <i className="ri-logout-box-line"></i>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden min-h-screen pb-20">
        {/* Mobile Header */}
        <header className="bg-white border-b border-neutral-200 sticky top-0 z-40">
          <div className="px-4 py-4 flex items-center justify-between">
            <Link to={`${basePrefix}/log`} className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-accent-600 rounded-lg flex items-center justify-center">
                <i className="ri-heart-pulse-fill text-white text-lg"></i>
              </div>
              <span className="text-lg font-bold text-neutral-900">LuminousHealth</span>
            </Link>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-neutral-700 cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="border-t border-neutral-200 bg-white">
              <nav className="p-4 space-y-1">
                {desktopNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer ${
                      isActive(item.path)
                        ? 'bg-primary-50 text-primary-600 font-medium'
                        : 'text-neutral-700'
                    }`}
                  >
                    <i className={`${item.icon} text-xl`}></i>
                    <span>{item.label}</span>
                  </Link>
                ))}
                {isDemo ? (
                  <Link
                    to="/signup"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium cursor-pointer"
                  >
                    <i className="ri-user-add-line text-xl"></i>
                    <span>Sign Up to Save</span>
                  </Link>
                ) : (
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-neutral-700 cursor-pointer"
                  >
                    <i className="ri-logout-box-line text-xl"></i>
                    <span>Logout</span>
                  </button>
                )}
              </nav>
            </div>
          )}
        </header>

        {/* Mobile Content */}
        <main>
          <Outlet />
        </main>

        {/* Mobile Bottom Navigation */}
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 z-50">
          <div className="flex items-center justify-around px-2 py-2">
            {mobileNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all cursor-pointer ${
                  isActive(item.path)
                    ? 'text-primary-600 font-semibold'
                    : 'text-neutral-600'
                }`}
              >
                <span className="text-sm">{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>
      </div>

      <SaveDemoDataModal 
        isOpen={showSaveModal} 
        onClose={() => setShowSaveModal(false)} 
      />
    </div>
  );
};

export default DashboardLayout;
